'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface FeaturedTemplate {
  id: string;
  name: string;
  platform: string;
  category: string | null;
  price: number;
  rating: number;
  reviews_count: number;
  downloads_count: number;
  thumbnail_url: string | null;
  tags: string[];
}

interface FeaturedTemplatesProps {
  templates: FeaturedTemplate[];
}

const platformColors: Record<string, string> = {
  shopify: '#96BF48',
  wordpress: '#21759B',
  woocommerce: '#7F54B3',
  html: '#E8633A',
  nextjs: '#000000',
  webflow: '#4353FF',
};

function StarRating({ rating, reviews }: {rating: number;reviews: number;}) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) =>
        <Icon
          key={star}
          name="StarIcon"
          size={12}
          variant={star <= Math.floor(rating) ? 'solid' : 'outline'}
          className={star <= Math.floor(rating) ? 'star-filled' : 'star-empty'} />

        )}
      </div>
      <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>
        {rating.toFixed(1)} ({reviews})
      </span>
    </div>);

}

export default function FeaturedTemplates({ templates }: FeaturedTemplatesProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.08 }
    );
    sectionRef.current?.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-24 lg:py-32"
      style={{ background: 'var(--bg-secondary)' }}>
      
      <div className="max-w-8xl mx-auto px-6">
        <div className="reveal flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
          <div>
            <span
              className="text-2xs font-bold uppercase tracking-[0.2em] mb-3 block"
              style={{ color: 'var(--accent)' }}>
              
              Handpicked for You
            </span>
            <h2
              className="font-display font-light text-4xl lg:text-6xl leading-tight"
              style={{ color: 'var(--text-primary)' }}>
              
              Featured{' '}
              <span className="italic">templates.</span>
            </h2>
          </div>
          <Link
            href="/templates"
            className="btn-secondary shrink-0">
            
            View All
            <Icon name="ArrowRightIcon" size={14} />
          </Link>
        </div>

        {/* Empty State */}
        {templates.length === 0 ? (
          <div className="reveal text-center py-16 px-6 rounded-2xl" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-light)' }}>
              <Icon name="RectangleStackIcon" size={32} style={{ color: 'var(--accent)' }} />
            </div>
            <h3 className="font-display font-medium text-xl mb-2" style={{ color: 'var(--text-primary)' }}>
              No Templates Yet
            </h3>
            <p className="text-sm max-w-md mx-auto" style={{ color: 'var(--text-secondary)' }}>
              Featured templates will appear here once they are published.
            </p>
          </div>
        ) : (
          /* Grid */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((tpl, i) => {
              const platformColor = platformColors[tpl.platform.toLowerCase()] || '#6B7280';
              const imageUrl = tpl.thumbnail_url || '/assets/images/no_image.png';
              const altText = `${tpl.name} ${tpl.platform} template${tpl.category ? ` for ${tpl.category}` : ''}`;
              
              return (
                <Link
                  key={tpl.id}
                  href="/template-detail"
                  className={`reveal template-card rounded-2xl overflow-hidden group`}
                  style={{ animationDelay: `${i * 80}ms` }}
                  onMouseEnter={() => setHoveredId(tpl.id)}
                  onMouseLeave={() => setHoveredId(null)}>
                  
                  {/* Image */}
                  <div className="card-img-wrap relative h-48">
                    <AppImage
                      src={imageUrl}
                      alt={altText}
                      fill
                      className="object-cover" />
                  
                    {/* Overlay on hover */}
                    <div
                      className="absolute inset-0 flex items-center justify-center gap-3 transition-opacity duration-300"
                      style={{ opacity: hoveredId === tpl.id ? 1 : 0, background: 'rgba(13,17,23,0.6)' }}>
                    
                      <span className="btn-primary text-xs py-2 px-4">Quick View</span>
                    </div>
                    {/* Badges */}
                    <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                      {tpl.is_featured && (
                        <span
                          className="badge"
                          style={{ background: 'var(--accent)', color: 'white' }}>
                          Featured
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <span
                          className="badge mb-1.5"
                          style={{
                            background: `${platformColor}18`,
                            color: platformColor
                          }}>
                          {tpl.platform.charAt(0).toUpperCase() + tpl.platform.slice(1)}
                        </span>
                        <h3 className="font-semibold text-base" style={{ color: 'var(--text-primary)' }}>
                          {tpl.name}
                        </h3>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="price-tag text-lg">${tpl.price.toFixed(0)}</p>
                      </div>
                    </div>

                    <StarRating rating={tpl.rating} reviews={tpl.reviews_count} />

                    {tpl.tags && tpl.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {tpl.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="platform-tag text-2xs">{tag}</span>
                        ))}
                      </div>
                    )}

                    <div
                      className="flex items-center gap-1.5 mt-3 pt-3"
                      style={{ borderTop: '1px solid var(--border)' }}>
                      <Icon name="ArrowDownTrayIcon" size={12} style={{ color: 'var(--text-muted)' } as React.CSSProperties} />
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        {tpl.downloads_count.toLocaleString()} downloads
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </section>);

}